/** How the audience profile's numbers are said in the plan summary. */

/** An AIS index of 1.81 means 1.8 times as likely as the country average. */
export const formatIndex = (index: number): string =>
  Number.isFinite(index) && index > 0 ? `${index.toFixed(1)}×` : '–';

/** A 0–1 share as a whole percentage. */
export const formatShare = (share: number): string =>
  Number.isFinite(share) && share >= 0 ? `${Math.round(share * 100)}%` : '–';

/** 11100000 -> "11.1M", 536000 -> "536K". Blank for no data. */
export const formatCount = (count?: number): string => {
  if (!count || !Number.isFinite(count) || count <= 0) return '';
  if (count >= 1_000_000) return `${+(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${Math.round(count / 1_000)}K`;
  return String(count);
};

/**
 * The de-duplication headline, or null when there is nothing to say: no total,
 * or so little overlap (under 5%) that it is within the API's rounding.
 */
export const overlapHeadline = (summed: number, unique: number): string | null => {
  if (!(unique > 0) || !(summed > 0) || summed < unique * 1.05) return null;
  const doubled = Math.round((1 - unique / summed) * 100);
  return `Together they reach about ${formatCount(unique)} people. Counted one by one they add up to ${formatCount(summed)}, so about ${doubled}% of that is the same people counted twice.`;
};

export interface ContainedAudience {
  audience: string;
  within: Array<{ name: string; share: number }>;
}

/**
 * High-overlap pairs grouped by the smaller audience, in first-seen order.
 * A live NL plan flagged 8 of its 10 pairs; one line per pair buried the
 * point, which is that a few small audiences sit inside the broad ones.
 */
export const groupHighOverlap = (
  pairs: Array<{ smaller: string; larger: string; share_of_smaller: number }>,
): ContainedAudience[] => {
  const groups = new Map<string, ContainedAudience>();
  pairs.forEach((p) => {
    const group = groups.get(p.smaller) || { audience: p.smaller, within: [] };
    group.within.push({ name: p.larger, share: p.share_of_smaller });
    groups.set(p.smaller, group);
  });
  return Array.from(groups.values());
};

export interface LayeringStep {
  name: string;
  reach: number;
  added: number;
  added_share: number;
  cumulative: number;
  adds_little: boolean;
  /** Too small to measure against totals this coarsely rounded. */
  uncertain?: boolean;
  in_scenarios?: boolean;
}

/**
 * The one-line answer to "what do the other audiences add?", or null when
 * there is no ladder to speak of.
 */
export const layeringHeadline = (steps: LayeringStep[]): string | null => {
  if (steps.length < 2) return null;
  const first = steps[0];
  const total = steps[steps.length - 1].cumulative;
  if (!(first.added > 0) || !(total > 0)) return null;
  const others = steps.length - 1;
  const gain = Math.round((total / first.added - 1) * 100);
  const lead = `${first.name} alone reaches about ${formatCount(first.added)} people.`;
  const rest = `Adding the other ${others} audience${others === 1 ? '' : 's'}`;
  return gain < 5
    ? `${lead} ${rest} adds almost no one new: they are mostly the same people.`
    : `${lead} ${rest} brings that to about ${formatCount(total)} (+${gain}%).`;
};

/**
 * Whether a report link may be rendered: https to Google Ads only. The link
 * comes from the API through session storage, so it is checked, not trusted.
 */
export const isFinderUrl = (url?: string): url is string => {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && parsed.hostname === 'ads.google.com';
  } catch {
    return false;
  }
};

/** A pair of suggested audiences that are mostly the same people, by id. */
export interface CandidatePair {
  smaller: string;
  larger: string;
  share_of_smaller: number;
}

/**
 * For each selected audience that is mostly inside another selected one, the
 * audiences it sits inside. Only pairs where both are selected count: the
 * warning is about the choice being made, not about what is on offer.
 */
export const duplicateWarnings = (
  selected: Set<string>,
  pairs: CandidatePair[],
): Map<string, Array<{ id: string; share: number }>> => {
  const out = new Map<string, Array<{ id: string; share: number }>>();
  pairs.forEach((p) => {
    if (!selected.has(p.smaller) || !selected.has(p.larger)) return;
    const list = out.get(p.smaller) || [];
    list.push({ id: p.larger, share: p.share_of_smaller });
    out.set(p.smaller, list);
  });
  return out;
};
